import { skipToken } from '@tanstack/react-query';
import { graphql } from 'generated/gql/account-manager';
import { useAuthorizationHeader } from 'graphql/helpers';
import { useAmQuery } from 'graphql/useAmQuery';
import { useParams } from 'react-router';
import { useCurrentUser } from './useCurrentUser';

const accountQueryDocument = graphql(`
  query account($id: Int!) {
    account(accountId: $id) {
      id
      name
      isAgency
      owner {
        carrotAccountStatus
        carrotCrmConfig {
          teamId
        }
      }
      agency {
        account {
          id
        }
      }
      paySubscription {
        usage
        limits
      }
      sites {
        id
        name
        platformSiteId
        mappedDomain
        subdomain
        category
        registeredAt
        blockTheme
        owner {
          wordpressUserId
          wordpressUsername
        }
      }
      onboardingStatus
      twilioSubaccount {
        id
      }
    }
  }
`);

function useAccount() {
  const currentUserQuery = useCurrentUser();
  const { accountId } = useParams();
  const accountIdWithFallback =
    accountId ?? currentUserQuery.data?.user?.activeAccount?.id;
  const requestHeaders = useAuthorizationHeader();

  const enabled =
    // Wait until the GraphQL request will be authenticated
    requestHeaders &&
    // Only run when an account ID is found
    accountIdWithFallback;

  return useAmQuery(
    accountQueryDocument,
    enabled ? { id: Number(accountIdWithFallback) } : skipToken
  );
}

/**
 * WARN: only use this in places where the account is guaranteed to exist,
 * e.g., pages that won't be rendered unless the account has passed the checks
 * in AuthorizedPage.
 *
 * @throws {Error} if the account is not found
 */
function useGuaranteedAccount() {
  const accountQuery = useAccount();

  const account = accountQuery.data?.account;
  if (!account) {
    throw new Error('An account is required to use this hook');
  }

  return account;
}

export { useAccount, useGuaranteedAccount };
